<template>
  <div v-if="!isInitialized" class="d-flex justify-content-center align-items-center" style="min-height: 50vh;">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
  
  <div v-else class="community-page">
    <!-- Page Header -->
    <CommunityHeader
      :is-authenticated="isAuthenticated"
      :is-admin="user?.role === 'admin'"
      :seeding="seeding"
      @create-post="showCreateModal = true"
      @seed-data="seedDemoData"
    />

    <div class="container mt-4">
      <div class="row">
        <!-- Sidebar -->
        <div class="col-lg-3">
          <!-- Topic Circles -->
          <TopicCircles
            :topics="topics"
            :selected-topic="selectedTopic"
            :topic-post-counts="topicPostCounts"
            @select-topic="selectTopic"
          />

          <!-- User Achievements -->
          <UserAchievements
            :is-authenticated="isAuthenticated"
            :user-achievements="userAchievements"
          />

          <!-- Health Videos -->
          <HealthVideos
            :videos="featuredVideos"
            @watch-video="watchVideo"
          />
        </div>

        <!-- Main Content -->
        <div class="col-lg-9">
          <!-- Topic Filter Bar -->
          <TopicFilterBar
            :topics="topics"
            :selected-topic="selectedTopic"
            @select-topic="selectTopic"
          />

          <!-- Search Bar -->
          <SearchBar
            v-model:search-query="searchQuery"
            :results-count="filteredPosts.length"
            @clear="clearSearch"
          />

          <!-- Posts List -->
          <PostsList
            :posts="paginatedPosts"
            :loading="loading"
            :is-authenticated="isAuthenticated"
            :user="user"
            :get-topic-name="getTopicName"
            :format-time="formatTime"
            @create-post="showCreateModal = true"
            @like-post="toggleLike"
            @show-rating="showRatingModal"
          />

          <!-- Pagination -->
          <PaginationControls
            :current-page="currentPage"
            :total-pages="totalPages"
            :total-items="filteredPosts.length"
            :items-per-page="postsPerPage"
            @page-change="goToPage"
          />
        </div>
      </div>
    </div>

    <!-- Create Post Modal -->
    <CreatePostModal
      :show-modal="showCreateModal"
      :topics="topics"
      :is-submitting="posting"
      @close="showCreateModal = false"
      @submit="createPost"
    />

    <!-- Rating Modal -->
    <RatingModal
      :show-modal="showRatingModalFlag"
      :post="currentRatingPost"
      :is-submitting="submittingRating"
      @close="showRatingModalFlag = false"
      @submit="handleRatingSubmit"
    />
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { useCommunityPreload } from '@/composables/useCommunityPreload'
import { CommunityService } from '@/services/communityService'
import CommunityHeader from './component/community/CommunityHeader.vue'
import TopicCircles from './component/community/TopicCircles.vue'
import UserAchievements from './component/community/UserAchievements.vue'
import HealthVideos from './component/community/HealthVideos.vue'
import TopicFilterBar from './component/community/TopicFilterBar.vue'
import SearchBar from './component/community/SearchBar.vue'
import PostsList from './component/community/PostsList.vue'
import PaginationControls from './component/community/PaginationControls.vue'
import CreatePostModal from './component/community/CreatePostModal.vue'
import RatingModal from './component/community/RatingModal.vue'

export default {
  name: 'Community',
  components: {
    CommunityHeader,
    TopicCircles,
    UserAchievements,
    HealthVideos,
    TopicFilterBar,
    SearchBar,
    PostsList,
    PaginationControls,
    CreatePostModal,
    RatingModal
  },
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    const communityPreload = useCommunityPreload()
    return {
      user,
      isAuthenticated,
      initAuth,
      communityPreload
    }
  },
  data() {
    return {
      communityService: new CommunityService(),
      loading: false,
      posting: false,
      posts: [],
      selectedTopic: null,
      showCreateModal: false,
      showRatingModalFlag: false,
      currentRatingPost: null,
      submittingRating: false,

      // Search
      searchQuery: '',

      // Pagination
      currentPage: 1,
      postsPerPage: 10,

      // Topics
      topics: [
        { id: 'pregnancy', name: 'Pregnancy & Parenting', icon: 'bi bi-heart-fill', postCount: 0 },
        { id: 'weight-loss', name: 'Weight Loss Tips', icon: 'bi bi-speedometer2', postCount: 0 },
        { id: 'health', name: 'Physical Health', icon: 'bi bi-shield-plus', postCount: 0 },
        { id: 'stress', name: 'Stress Relief', icon: 'bi bi-peace', postCount: 0 },
        { id: 'beauty', name: 'Beauty & Care', icon: 'bi bi-gem', postCount: 0 }
      ],



      // User achievements
      userAchievements: {
        points: 0,
        level: 1,
        badges: []
      },

      // Health videos
      featuredVideos: [],

      // Demo data seeding
      seeding: false,

      // All posts for counting (unfiltered)
      allPosts: [],

      // Component initialization flag
      isInitialized: false,

      // Preload interval
      preloadCheckInterval: null
    }
  },
  
  computed: {
    topicPostCounts() {
      const counts = {}
      if (this.allPosts && Array.isArray(this.allPosts)) {
        this.topics.forEach(topic => {
          counts[topic.id] = this.allPosts.filter(post => post && post.topic === topic.id).length
        })
      }
      return counts
    },

    // Filter posts based on search query
    filteredPosts() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.posts
      }

      const query = this.searchQuery.toLowerCase().trim()
      return this.posts.filter(post => {
        // Search in title
        if (post.title && post.title.toLowerCase().includes(query)) {
          return true
        }

        // Search in content
        if (post.content && post.content.toLowerCase().includes(query)) {
          return true
        }

        // Search in tags
        if (post.tags && Array.isArray(post.tags)) {
          const hasMatchingTag = post.tags.some(tag =>
            tag.toLowerCase().includes(query)
          )
          if (hasMatchingTag) {
            return true
          }
        }

        // Search in author name
        if (post.authorName && post.authorName.toLowerCase().includes(query)) {
          return true
        }

        return false
      })
    },

    // Calculate total pages
    totalPages() {
      return Math.ceil(this.filteredPosts.length / this.postsPerPage)
    },

    // Get posts for current page
    paginatedPosts() {
      const start = (this.currentPage - 1) * this.postsPerPage
      const end = start + this.postsPerPage
      return this.filteredPosts.slice(start, end)
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1 // Reset to first page when searching
    }
  },

  mounted() {
    try {
      this.initAuth()

      // Mark as initialized immediately to show the UI
      this.isInitialized = true

      // Check if data is already preloaded
      if (this.communityPreload.isLoaded.value) {
        console.log('[Community] Using preloaded data')
        this.usePreloadedData()
      } else if (this.communityPreload.isLoading.value) {
        console.log('[Community] Preload in progress, waiting...')
        this.waitForPreload()
      } else {
        console.log('[Community] Preload not started, loading data now...')
        this.loadPosts()
        this.loadUserAchievements()
        this.loadFeaturedVideos()
        this.loadAllPostsForCounting()
      }
    } catch (error) {
      console.error('Error in mounted hook:', error)
      this.isInitialized = true // Still mark as initialized to prevent stuck state
    }
  },
  
  beforeUnmount() {
    // Cleanup any pending operations
    this.loading = false
    this.seeding = false

    // Clear preload check interval if exists
    if (this.preloadCheckInterval) {
      clearInterval(this.preloadCheckInterval)
      this.preloadCheckInterval = null
    }
  },
  
  methods: {
    // Wait for preload to complete
    async waitForPreload() {
      const maxWaitTime = 5000 // Max 5 seconds
      const startTime = Date.now()

      this.preloadCheckInterval = setInterval(() => {
        if (this.communityPreload.isLoaded.value) {
          clearInterval(this.preloadCheckInterval)
          this.preloadCheckInterval = null
          console.log('[Community] Preload completed, using data')
          this.usePreloadedData()
        } else if (Date.now() - startTime > maxWaitTime) {
          clearInterval(this.preloadCheckInterval)
          this.preloadCheckInterval = null
          console.log('[Community] Preload timeout, loading data directly')
          this.loadPosts()
          this.loadUserAchievements()
          this.loadFeaturedVideos()
          this.loadAllPostsForCounting()
        }
      }, 100)
    },

    // Use preloaded data from the composable
    usePreloadedData() {
      if (this.communityPreload.posts.value && this.communityPreload.posts.value.length > 0) {
        this.posts = [...this.communityPreload.posts.value]
        console.log(`[Community] Loaded ${this.posts.length} posts from preload`)
      }

      if (this.communityPreload.allPosts.value && this.communityPreload.allPosts.value.length > 0) {
        this.allPosts = [...this.communityPreload.allPosts.value]
        console.log(`[Community] Loaded ${this.allPosts.length} total posts from preload`)
      }

      if (this.communityPreload.videos.value && this.communityPreload.videos.value.length > 0) {
        this.featuredVideos = [...this.communityPreload.videos.value]
        console.log(`[Community] Loaded ${this.featuredVideos.length} videos from preload`)
      }

      // Still load user achievements as they are user-specific
      this.loadUserAchievements()
    },

    async loadAllPostsForCounting() {
      try {
        const result = await this.communityService.getPosts(null) // Load all posts
        if (result.success && result.posts) {
          this.allPosts = result.posts
        } else {
          this.allPosts = []
        }
      } catch (error) {
        console.error('Failed to load all posts for counting:', error)
        this.allPosts = []
      }
    },
    
    async loadPosts() {
      this.loading = true
      try {
        const result = await this.communityService.getPosts(this.selectedTopic)
        if (result.success && result.posts) {
          this.posts = result.posts
        } else {
          this.posts = []
        }
      } catch (error) {
        console.error('Failed to load posts:', error)
        this.posts = []
      } finally {
        this.loading = false
      }
    },
    
    async loadUserAchievements() {
      if (!this.isAuthenticated || !this.user?.uid) return
      
      try {
        const result = await this.communityService.getUserAchievements(this.user.uid)
        if (result.success) {
          this.userAchievements = result.achievements
        }
      } catch (error) {
        console.error('Failed to load achievements:', error)
      }
    },
    
    async loadFeaturedVideos() {
      try {
        const result = await this.communityService.getHealthVideos()
        if (result.success) {
          this.featuredVideos = result.videos
        }
      } catch (error) {
        console.error('Failed to load videos:', error)
      }
    },
    
    selectTopic(topicId) {
      this.selectedTopic = topicId
      this.currentPage = 1 // Reset to first page when changing topic
      this.loadPosts()
    },

    // Clear search
    clearSearch() {
      this.searchQuery = ''
      this.currentPage = 1
    },

    // Watch video
    watchVideo(video) {
      console.log('Watch video:', video)
      // TODO: Implement video player modal or navigation
      alert(`Video player coming soon for: ${video.title}`)
    },

    // Go to specific page
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page

      // Scroll to top of posts list
      const postsContainer = document.querySelector('.col-lg-9')
      if (postsContainer) {
        postsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    
    getTopicName(topicId) {
      const topic = this.topics.find(t => t.id === topicId)
      return topic ? topic.name : 'Other'
    },
    
    async createPost(postData) {
      if (!this.isAuthenticated) return
      
      this.posting = true
      try {
        const result = await this.communityService.createPost(this.user.uid, postData)
        if (result.success) {
          this.showCreateModal = false
          await this.loadPosts()
          
          await this.communityService.updateUserAchievements(this.user.uid, 'post')
          await this.loadUserAchievements()
        } else {
          console.error('Failed to create post:', result.error)
        }
      } catch (error) {
        console.error('Failed to create post:', error)
      } finally {
        this.posting = false
      }
    },
    
    async toggleLike(post) {
      if (!this.isAuthenticated) return
      
      try {
        const isLiked = post.likes && post.likes.includes(this.user.uid)
        
        if (isLiked) {
          await this.communityService.unlikePost(post.id, this.user.uid)
          post.likes = post.likes.filter(id => id !== this.user.uid)
        } else {
          await this.communityService.likePost(post.id, this.user.uid)
          if (!post.likes) post.likes = []
          post.likes.push(this.user.uid)
          
          await this.communityService.updateUserAchievements(this.user.uid, 'like')
        }
      } catch (error) {
        console.error('Failed to toggle like:', error)
      }
    },
    
    showRatingModal(post) {
      this.currentRatingPost = post
      this.showRatingModalFlag = true
    },
    
    async handleRatingSubmit(ratingData) {
      console.log('handleRatingSubmit called with:', ratingData)
      console.log('User authenticated:', this.isAuthenticated)
      console.log('User object:', this.user)
      
      if (!this.isAuthenticated) {
        alert('Please log in to rate posts.')
        return
      }
      
      if (!this.user || !this.user.uid) {
        alert('User information not available. Please try logging in again.')
        return
      }
      
      this.submittingRating = true
      try {
        console.log('Calling ratePost service with:', {
          postId: ratingData.post.id,
          userId: this.user.uid,
          rating: ratingData.rating,
          review: ratingData.review
        })
        
        const result = await this.communityService.ratePost(
          ratingData.post.id,
          this.user.uid,
          ratingData.rating,
          ratingData.review
        )
        
        console.log('Service returned:', result)
        
        if (result.success) {
          this.showRatingModalFlag = false
          
          // Update the specific post in the local array immediately for better UX
          const postIndex = this.posts.findIndex(p => p.id === ratingData.post.id)
          if (postIndex >= 0) {
            this.posts[postIndex].averageRating = result.averageRating || 0
            this.posts[postIndex].totalRatings = result.totalRatings || 1
            console.log('Updated post locally:', {
              postId: ratingData.post.id,
              averageRating: this.posts[postIndex].averageRating,
              totalRatings: this.posts[postIndex].totalRatings
            })
          }
          
          // Reload posts to get fresh data from database
          console.log('Reloading posts after rating submission...')
          await this.loadPosts()
          
          // Check if the rating was properly saved
          const updatedPost = this.posts.find(p => p.id === ratingData.post.id)
          if (updatedPost) {
            console.log('Post after reload:', {
              postId: updatedPost.id,
              averageRating: updatedPost.averageRating,
              totalRatings: updatedPost.totalRatings,
              ratingsArray: updatedPost.ratings?.length || 0
            })
          }
          
          // Show success message
          alert('Rating submitted successfully!')
          
          // Update achievements
          try {
            await this.communityService.updateUserAchievements(this.user.uid, 'rate')
            await this.loadUserAchievements()
          } catch (achievementError) {
            console.warn('Failed to update achievements:', achievementError)
          }
          
        } else {
          console.error('Failed to submit rating:', result.error)
          alert('Failed to submit rating: ' + (result.error || 'Unknown error'))
        }
      } catch (error) {
        console.error('Failed to submit rating:', error)
        alert('An error occurred while submitting your rating: ' + error.message)
      } finally {
        this.submittingRating = false
      }
    },
    
    formatTime(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
      const diffMinutes = Math.floor(diffTime / (1000 * 60))
      
      if (diffMinutes < 1) return 'Just now'
      if (diffMinutes < 60) return `${diffMinutes}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString('en-US')
    },
    
    // Seed demo data for development
    async seedDemoData() {
      if (!this.isAuthenticated || this.user?.role !== 'admin') {
        alert('Only admins can seed demo data')
        return
      }
      
      this.seeding = true
      try {
        const result = await this.communityService.seedDemoData()
        if (result.success) {
          alert('Demo data created successfully! The page will refresh to show new posts.')
          // Refresh the page to show new data
          window.location.reload()
        } else {
          alert('Failed to create demo data: ' + result.error)
        }
      } catch (error) {
        console.error('Error seeding demo data:', error)
        alert('Error creating demo data. Check console for details.')
      } finally {
        this.seeding = false
      }
    }
  }
}
</script>

<style scoped>
/* Main container styles only - component-specific styles are in child components */
</style>
